import { useCallback, useEffect, useState } from "react";
import { useAppSelector } from "../../hooks/redux";
import store, { RootState } from "../../store/store";
import Button from "../common/Button";
import { gameActions } from "../Game/gameSlice";
import "./User.scss";
import { useNavigate } from "react-router-dom";
import { LocalStorageKeys } from "../../models/LocalStorageKeys";
import { userActions } from "./userSlice";
import React from "react";
import PlayerPicture from "../Players/PlayerPicture";
import UserHeading from "./UserHeading";

const DISCORD_ID_MAX_LENGTH = 22;
const TOKEN_MAX_LENGTH = 10;
const USERNAME_MAX_LENGTH = 16;
const USERNAME_MIN_LENGTH = 3;

export default function UserSettings() {

  const navigate = useNavigate();

  const gameState = useAppSelector((state: RootState) => state.game);
  const userState = useAppSelector((state: RootState) => state.user);
  const localToken = localStorage.getItem(LocalStorageKeys.TOKEN_KEY);

  const [isAvatarClickEnabled, setAvatarClickEnabled] = React.useState(true);
  const [formName, setFormName] = useState(userState.user.name || '');
  const [formDiscordId, setFormDiscordId] = useState(userState.user.discordId || '');
  const [formToken, setFormToken] = useState(localToken || '');
  const [formTelegramId, setFormTelegramId] = useState(userState.user.telegramId || '');
  const [formSoundsEnabled, setFormSoundsEnabled] = useState(userState.user.soundsEnabled);
  const [formDirectNotifications, setFormDirectNotifications] = useState(userState.user.directNotificationsEnabled);
  const [formConfirmActionEnabled, setFormConfirmActionEnabled] = useState(userState.user.confirmActionEnabled);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const [isClicked, setIsClicked] = useState(false);

  function handleSoundChange(enabled: boolean) {
    store.dispatch(userActions.soundEnabled(enabled));
    setFormSoundsEnabled(enabled);
  }

  function handleConfirmActionsChange(enabled: boolean) {
    store.dispatch(userActions.confirmActionEnabled(enabled));
    setFormConfirmActionEnabled(enabled);
  }

  function handleDirectNotificationsChange(enabled: boolean) {
    store.dispatch(userActions.directNotificationsChange(enabled));
    setFormDirectNotifications(enabled);
  }

  useEffect(() => {
    document.addEventListener("keydown", keyPressed, false);

    return () => {
      document.removeEventListener("keydown", keyPressed, false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const keyPressed = useCallback((event) => {
    switch (event.key) {
      case 'Escape':
        navigateToMatchmaking();
        break;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!localToken) {
      navigate(`/new`, { replace: true });
      return;
    }

    if (gameState.isConnected) {

      store.dispatch(gameActions.sendToken({
        token: localToken,
        gameId: undefined,
        isSettings: true,
      }));

    }
  }, [gameState.isConnected, localToken]);

  useEffect(() => {

    if (userState.user.id) {
      if (!formName && userState.user.name) {
        setFormName(userState.user.name);
      }
      if (!formDiscordId && userState.user.discordId) {
        setFormDiscordId(userState.user.discordId);
      }
      if (!formTelegramId && userState.user.telegramId) {
        setFormTelegramId(userState.user.telegramId);
      }
      if (userState.user.soundsEnabled !== undefined) {
        setFormSoundsEnabled(userState.user.soundsEnabled);
      }
      if (userState.user.confirmActionEnabled !== undefined) {
        setFormConfirmActionEnabled(userState.user.confirmActionEnabled);
      }
      if (userState.user.directNotificationsEnabled !== undefined) {
        setFormDirectNotifications(userState.user.directNotificationsEnabled);
      }
    }

  }, [userState.user.id, userState.user.telegramId, userState.user.soundsEnabled]);

  const isSubmitDisabled =
    hasSubmitted ||
    !formName ||
    formName.length < USERNAME_MIN_LENGTH ||
    formName.length > USERNAME_MAX_LENGTH
    ;

  function navigateToMatchmaking() {
    navigate(`/`, { replace: true });
  }

  function handleReset() {
    if (window.confirm('Are you sure you want to reset your player token?')) {
      navigate(`/reset`, { replace: true });
    }
  }

  function handleSubmit(e: any) {
    e.preventDefault();

    if (!gameState.isConnected) {
      return;
    }

    localStorage.setItem(LocalStorageKeys.DISCORD_ID_KEY, formDiscordId);
    localStorage.setItem(LocalStorageKeys.NAME_KEY, formName);

    store.dispatch(gameActions.sendUser({
      id: userState.user.id,
      name: formName,
      //discordId:  formDiscordId,
      telegramId: formTelegramId,
      soundsEnabled: formSoundsEnabled,
      confirmActionEnabled: formConfirmActionEnabled,
      dixitAvatar: userState.user.dixitAvatar,
      directNotificationsEnabled: formDirectNotifications,
    }));

    setHasSubmitted(true);
    navigateToMatchmaking();
  }

  async function copyText(text: string) {
    try {
      setIsClicked(true);
      setTimeout(() => setIsClicked(false), 100);

      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  }

  const playerData = {
    username: formName,
    picture: userState.user.picture,
  };

  function avatarClick() {
    if (isAvatarClickEnabled && gameState.isConnected) {
      store.dispatch(gameActions.changeAvatar({
        gameId: null,
        playerId: userState.user.id,
      }));

      setAvatarClickEnabled(false);

      // allow to change avatar after some timeout
      setTimeout(() => {
        setAvatarClickEnabled(true);
      }, 200)
    }
  }

  return (
    <>
      <UserHeading className="player-form_heading" text="Settings" onClose={navigateToMatchmaking} />
      <div className="player-form">
        <div className="player-form_content">

          <form onSubmit={handleSubmit}>
            <fieldset>

              <div className="player-form_avatar a" onClick={() => avatarClick()}>
                <PlayerPicture player={playerData} size={72} />
              </div>

              <label>
                <div className="form-label">name:</div>
                <input
                  readOnly={false}
                  maxLength={USERNAME_MAX_LENGTH}
                  onChange={e => setFormName(e.target.value)}
                  type="text"
                  name="formName"
                  value={`${formName || ''}`}
                  placeholder=""
                />
              </label>
              <label>
                <div className="player-form_token">
                  <div className="pointer flex-row" >
                    <div className="form-label">player token:</div>
                    <div className={`no-select form-label_icon ${isClicked ? 'clicked' : ''}`} onClick={() => copyText(formToken)}>
                      <img src={'/copy.png'} alt="copy text"></img>
                    </div>
                  </div>
                </div>
                <div>
                  <input
                    readOnly={true}
                    maxLength={TOKEN_MAX_LENGTH}
                    disabled={true}
                    onChange={e => setFormToken(e.target.value)}
                    type="text"
                    name="formToken"
                    value={formToken}
                    placeholder=""
                  />
                </div>

              </label>
              <label>
                <div className="form-label">discord Id:</div>
                <input
                  readOnly={false}
                  maxLength={DISCORD_ID_MAX_LENGTH}
                  disabled={!!userState.user.discordId}
                  onChange={e => setFormDiscordId(e.target.value)}
                  type="text"
                  name="formDiscordId"
                  value={`${formDiscordId || ''}`}
                  placeholder=""
                />
              </label>
              <label>
                <div className="form-label">telegram Id:</div>
                <input
                  readOnly={true}
                  disabled={true}
                  onChange={e => setFormTelegramId(e.target.value)}
                  type="text"
                  name="formTelegramId"
                  value={`${formTelegramId || ''}`}
                  placeholder=""
                />
              </label>

              <label className="align-left">
                <input type="checkbox"
                  checked={formSoundsEnabled}
                  onChange={(e) => handleSoundChange(e.target.checked)}
                />
                <span>play sounds</span>
              </label>

              <label className="align-left">
                <input type="checkbox"
                  checked={formConfirmActionEnabled}
                  onChange={(e) => handleConfirmActionsChange(e.target.checked)}
                />
                <span>action confirmation popup</span>
              </label>

              <label className="align-left">
                <input type="checkbox"
                  checked={formDirectNotifications}
                  onChange={(e) => handleDirectNotificationsChange(e.target.checked)}
                />
                <span>telegram direct notifications <br />
                  <span className="note">will work only after you send <b>your player token</b> to the <a target="_blank" href="https://t.me/dixit_sfixit_bot">bot</a></span></span>
              </label>

              <span className="danger-text underline pointer" onClick={handleReset}>logout</span>

            </fieldset>


            <Button onClick={handleSubmit} text="Save" disabled={isSubmitDisabled} />
            <Button classes={'danger'} onClick={navigateToMatchmaking} text="Cancel" disabled={isSubmitDisabled} />
          </form>
        </div>
      </div>
    </>
  )
}